import * as React from 'react';
import { RecipientWell } from 'owa-readwrite-recipient-well-fabric/lib/components/PeoplePickerTypes';
import ReadWriteRecipientViewState from 'owa-recipient-common/lib/store/schema/ReadWriteRecipientViewState';
import RecipientWellWithFindControlViewState from 'owa-recipient-common/lib/store/schema/RecipientWellWithFindControlViewState';

import addReadWriteRecipients from 'owa-readwrite-recipient-well/lib/actions/addReadWriteRecipients';
import removeReadWriteRecipients from 'owa-readwrite-recipient-well/lib/actions/removeReadWriteRecipients';
import updateEditedRecipient from 'owa-readwrite-recipient-well/lib/actions/updateEditedRecipient';
import removeFindPeopleEntity from 'owa-recipient-common/lib/utils/removeFindPeopleEntity';

import { observer } from 'mobx-react-lite';

type PropsOf<T> = T extends React.Component<infer P> ? P : never;
type RecipientWellprops = PropsOf<RecipientWell>;
export type UncontrolledRecipientWellProps = Pick<
    RecipientWellprops,
    Exclude<
        keyof RecipientWellprops,
        | 'selectedItems'
        | 'defaultSelectedItems'
        | 'findControlViewState'
        | 'useAsControlledComponent'
    >
> & {
    viewState: RecipientWellWithFindControlViewState;
};

const UncontrolledRecipientWellInner = (
    props: UncontrolledRecipientWellProps,
    ref: React.Ref<RecipientWell>
) => {
    const onItemsAdded = React.useCallback(
        (recipients: ReadWriteRecipientViewState[]) => {
            const recipientsData = recipients.map(function(recipient: ReadWriteRecipientViewState) {
                return { isValid: recipient.isValid, newPersona: recipient.persona };
            });

            addReadWriteRecipients(props.viewState, recipientsData);
            props.onItemsAdded && props.onItemsAdded(recipients);
        },
        [props.viewState]
    );

    const onItemsRemoved = React.useCallback(
        (recipients: ReadWriteRecipientViewState[], fromExpand?: boolean) => {
            removeReadWriteRecipients(
                props.viewState,
                recipients,
                null /* selectToRight*/,
                !fromExpand
            );
            recipients.forEach(recipient => {
                removeFindPeopleEntity(props.viewState.findPeopleFeedbackManager, recipient);
            });
            props.onItemsRemoved && props.onItemsRemoved(recipients, fromExpand);
        },
        [props.viewState]
    );

    const onItemEdited = React.useCallback(
        (recipient: ReadWriteRecipientViewState, updatedRecipient: ReadWriteRecipientViewState) => {
            updateEditedRecipient(recipient, updatedRecipient);
            props.onItemEdited && props.onItemEdited(recipient, updatedRecipient);
        },
        [props.viewState]
    );

    return (
        <RecipientWell
            ref={ref}
            selectedItems={props.viewState.recipients}
            defaultSelectedItems={props.viewState.recipients.slice()}
            findControlViewState={props.viewState}
            useAsControlledComponent={true}
            {...props}
            inputProps={{ ...props.inputProps, defaultVisibleValue: props.viewState.queryString }}
            onItemsAdded={onItemsAdded}
            onItemsRemoved={onItemsRemoved}
            onItemEdited={onItemEdited}
        />
    );
};

/**
 * A component that renders a fabric recipient well against a viewstate,
 * and automatically updates the viewstate when the recipient well is interacted with.
 */
export const UncontrolledRecipientWell = observer(UncontrolledRecipientWellInner, {
    forwardRef: true,
});

export default UncontrolledRecipientWell;
